// WAAP Redirection — website redirection rules (match a request → Location +
// status code).
//
// PUBLIC Open-API surface (root conversant SDK, HMAC). Per-service rules under
//   GET    /waap/domains/:domainId/redirection            → BARE ARRAY of rules
//   POST   /waap/domains/:domainId/redirection            → create one rule (returns it)
//   PUT    /waap/domains/:domainId/redirection/:ruleId     → replace one rule (returns it)
//   DELETE /waap/domains/:domainId/redirection/:ruleId     → { status: "ok" }
// :domainId anchors the website; the BE resolves the vendor service from it.
//
// Shape differences vs the old private surface (kept LOCAL here per task rules —
// _shared.ts is not edited; its WaapRedirection/WaapRedirections/WaapAccessMatch
// describe the private shape and are intentionally NOT reused):
//   - List is a BARE ARRAY (`[]`), not `{ rules: [...] }`.
//   - Matching is two FIXED optional slots — `match_url_path` and
//     `match_query_string` — each `{ operator, patterns }`, instead of the
//     private free-form `matches[]` of `{ what, operator, patterns }`.
//   - Operators are restricted to the vendor MatchPattern set:
//     `equals | prefix | suffix | regex`.
//   - There is NO PATCH toggle: enable/disable is done via PUT with the full
//     body, flipping `enabled`.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, WAAP_AREAS } from './_shared';

// ---- Public Open-API DTOs (local — mirror the BE publicRedirectionDTO) ------

/** A fixed-slot match pattern. `operator` ∈ equals | prefix | suffix | regex. */
export interface WaapRedirectionMatch {
  operator: string;
  patterns: string[];
}

/** A redirection rule on the public Open-API surface. */
export interface WaapRedirectionRule {
  id: number;
  name: string;
  /** Match against the request URL path (omitted = not used). */
  match_url_path?: WaapRedirectionMatch | null;
  /** Match against the request query string (omitted = not used). */
  match_query_string?: WaapRedirectionMatch | null;
  /** Lower evaluates first. */
  priority: number;
  redirection_url: string;
  /** Redirect HTTP status (301 / 302 / 307 / 308). */
  status_code: number;
  enabled: boolean;
}

/** Create/update request body (the BE redirectionWriteInput; no `id`). */
export type WaapRedirectionInput = Omit<WaapRedirectionRule, 'id'>;

const redirectionPath = (domainId: string) => `/waap/domains/${domainId}/redirection`;

/** List a website's redirection rules (null on 400/404 = none/unconfigured). */
export function useWaapRedirections(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.redirections),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapRedirectionRule[] | null> => {
      try {
        const res = await serviceApiV3.get<WaapRedirectionRule[]>(redirectionPath(domainId as string));
        return res.data ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Create one redirection rule. Invalidates the list. */
export function useCreateWaapRedirection(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapRedirectionInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapRedirectionInput) => {
      const res = await serviceApiV3.post(redirectionPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.redirections) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Edit one redirection rule (PUT, full body replace). Invalidates the list. */
export function useUpdateWaapRedirection(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapRedirectionInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapRedirectionInput }) => {
      const res = await serviceApiV3.put(`${redirectionPath(domainId)}/${id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.redirections) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Enable/disable one redirection rule. The public surface has NO PATCH toggle,
 * so this re-PUTs the full rule with `enabled` flipped. Invalidates the list.
 */
export function useToggleWaapRedirection(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { rule: WaapRedirectionRule; enabled: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rule, enabled }: { rule: WaapRedirectionRule; enabled: boolean }) => {
      const body: WaapRedirectionInput = {
        name: rule.name,
        match_url_path: rule.match_url_path ?? null,
        match_query_string: rule.match_query_string ?? null,
        priority: rule.priority,
        redirection_url: rule.redirection_url,
        status_code: rule.status_code,
        enabled,
      };
      const res = await serviceApiV3.put(`${redirectionPath(domainId)}/${rule.id}`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.redirections) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete one redirection rule. Invalidates the list. */
export function useDeleteWaapRedirection(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(`${redirectionPath(domainId)}/${id}`);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.redirections) });
      options?.onSuccess?.(...args);
    },
  });
}
